import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { EnterpriseFormModal, FieldHint, type FormModalMode } from 'components/enterprise-shell';
import { SyutSelect } from 'components/ui';
import type { SelectOption } from 'components/ui/SyutSelect';
import BusinessUnitSelect from 'components/common/org/BusinessUnitSelect';
import { dineOpsService } from 'api/services/dineOpsService';
import type { DineOutletAdmin, DineRequisition, RequisitionItemOption, RequisitionPurpose } from 'api/types';
import { useRequisitionPurposes } from 'hooks/api/useDineOps';
import { REQUESTING_UNIT_TYPES, money, purposeOptions } from './requisitionLabels';

/**
 * ============================================================================
 * REQUISITION FORM (REQ-001, R1)
 * ============================================================================
 * What a department asks an outlet's kitchen for: the outlet that prepares it,
 * the department or cost centre it is for, why (a purpose from the tenant's
 * list, e.g. staff meal), when it is needed, and the dishes with quantities. A
 * staff meal is an ordinary dish × the headcount. Each line shows today's
 * transfer value (the dish's own transfer price, else the outlet's percentage
 * of its menu price); the value is fixed when the requisition is issued. No tax,
 * no bill. Editable only until it is sent to the kitchen; the outlet is fixed
 * once saved.
 * ============================================================================
 */

interface LineRow { key: string; itemId: string; quantity: string; note: string }

export interface RequisitionForm {
  dineOutletId: string;
  requestingUnitId: string;
  purpose: RequisitionPurpose;
  /** datetime-local value, '' = not said. */
  neededAt: string;
  remarks: string;
  lines: LineRow[];
}

let rowSeq = 0;
const newKey = () => `l-${Date.now()}-${rowSeq++}`;
const blankLine = (): LineRow => ({ key: newKey(), itemId: '', quantity: '1', note: '' });

const EMPTY_FORM: RequisitionForm = {
  dineOutletId: '', requestingUnitId: '', purpose: '', neededAt: '', remarks: '', lines: [],
};

/** ISO → the value a datetime-local input shows, in local time. */
const toLocalInput = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

interface Props {
  isOpen: boolean;
  mode: FormModalMode;
  /** For edit and copy, the requisition with its lines (GET by id). */
  record: DineRequisition | null;
  /** Active outlets (any can prepare a requisition). */
  outlets: DineOutletAdmin[];
  /** A new requisition starts at this outlet: the working outlet. */
  defaultOutletId?: string;
  tabId?: string;
  moduleName: string;
  createInstanceId?: string;
  onClose: () => void;
  onSaved: () => void;
  onRestoreModal: (mode: FormModalMode, instanceId?: string, recordCode?: string) => void;
}

const th: React.CSSProperties = { textAlign: 'left', padding: '0.4rem 0.5rem', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-color)' };
const td: React.CSSProperties = { padding: '0.35rem 0.5rem', verticalAlign: 'top', borderBottom: '1px solid var(--border-color)' };

/** The dishes table: a dish from the outlet's card, how many, a note for the kitchen. */
const LinesEditor: React.FC<{
  rows: LineRow[]; onChange: (rows: LineRow[]) => void; options: RequisitionItemOption[]; loading: boolean;
  disabled: boolean; error?: string; outletChosen: boolean;
}> = ({ rows, onChange, options, loading, disabled, error, outletChosen }) => {
  const byId = useMemo(() => new Map(options.map((o) => [o.itemId, o])), [options]);
  const selectOptions: SelectOption[] = useMemo(() => options.map((o) => ({
    value: o.itemId,
    label: `${o.itemName}${o.groupName ? ` · ${o.groupName}` : ''} — ${money(o.estimatedUnitPrice)}${o.isSoldOut ? ' (sold out)' : ''}`,
  })), [options]);
  const update = (key: string, patch: Partial<LineRow>) => onChange(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const total = rows.reduce((sum, r) => sum + (byId.get(r.itemId)?.estimatedUnitPrice ?? 0) * (Number(r.quantity) || 0), 0);

  return (
    <div data-field="lines">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <div className="form-label" style={{ margin: 0 }}>Dishes <span className="text-danger">*</span></div>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => onChange([...rows, blankLine()])} disabled={disabled || !outletChosen}>
          <IconSystem.add size={14} /> Add dish
        </button>
      </div>
      {error && <div className="form-error-message" style={{ marginBottom: '0.5rem' }}>{error}</div>}
      <div style={{ border: '1px solid var(--border-color)', borderRadius: '0.375rem', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr>
              <th style={th}>Dish</th>
              <th style={{ ...th, width: 90 }}>Qty</th>
              <th style={{ ...th, width: 220 }}>Note for the kitchen</th>
              <th style={{ ...th, width: 110, textAlign: 'right' }}>Value</th>
              <th style={{ ...th, width: 40 }} aria-label="Remove" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td style={{ ...td, color: 'var(--text-muted)' }} colSpan={5}>
                {outletChosen ? 'No dishes yet. Use Add dish.' : 'Choose the outlet first: the dishes come from its card.'}
              </td></tr>
            )}
            {rows.map((r) => {
              const o = byId.get(r.itemId);
              return (
                <tr key={r.key} data-requisition-line>
                  <td style={td}>
                    <SyutSelect options={selectOptions} value={r.itemId} onChange={(v) => update(r.key, { itemId: (v as string) || '' })}
                      placeholder={loading ? 'Loading the card…' : 'Choose a dish'} size="sm" isDisabled={disabled || loading}
                      menuPortalTarget={document.body} />
                  </td>
                  <td style={td}>
                    <input className="form-input" type="number" min={1} max={999} value={r.quantity} disabled={disabled}
                      onChange={(e) => update(r.key, { quantity: e.target.value })} aria-label="Quantity" />
                  </td>
                  <td style={td}>
                    <input className="form-input" value={r.note} maxLength={200} disabled={disabled} placeholder="Optional"
                      onChange={(e) => update(r.key, { note: e.target.value })} aria-label="Note for the kitchen" />
                  </td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {o ? money(o.estimatedUnitPrice * (Number(r.quantity) || 0)) : '—'}
                  </td>
                  <td style={td}>
                    <button type="button" className="modal-header-btn" disabled={disabled} title="Remove this dish"
                      onClick={() => onChange(rows.filter((x) => x.key !== r.key))}><IconSystem.x size={16} /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr>
                <td style={{ ...td, fontWeight: 600 }} colSpan={3}>Transfer value at today's prices (no tax)</td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }} data-requisition-total>{money(total)}</td>
                <td style={td} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <FieldHint>A staff meal is an ordinary dish × the headcount. The value is fixed when the requisition is issued.</FieldHint>
    </div>
  );
};

const DineOpsRequisitionFormModal: React.FC<Props> = ({
  isOpen, mode, record, outlets, defaultOutletId, tabId, moduleName, createInstanceId, onClose, onSaved, onRestoreModal,
}) => {
  const populateOnOpen = useCallback((m: FormModalMode, r: DineRequisition | null): RequisitionForm => {
    if (!r || m === 'create') {
      const start = outlets.some((o) => o.id === defaultOutletId) ? defaultOutletId! : '';
      return { ...EMPTY_FORM, dineOutletId: start, lines: [blankLine()] };
    }
    return {
      dineOutletId: r.dineOutletId,
      requestingUnitId: r.requestingUnitId,
      purpose: r.purpose,
      // A copy is for another day: when it is needed is asked again.
      neededAt: m === 'copy' ? '' : toLocalInput(r.neededAt),
      remarks: r.remarks ?? '',
      lines: (r.lines ?? []).map((l) => ({ key: newKey(), itemId: l.itemId, quantity: String(l.quantity), note: l.note ?? '' })),
    };
  }, [outlets, defaultOutletId]);

  const validate = useCallback((f: RequisitionForm): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!f.dineOutletId) errors.dineOutletId = 'Choose the outlet that will prepare it.';
    if (!f.requestingUnitId) errors.requestingUnitId = 'Choose the department it is for.';
    if (!f.purpose) errors.purpose = 'Choose the purpose.';
    if (f.remarks.trim().length > 500) errors.remarks = 'At most 500 characters.';
    const lines = f.lines.filter((l) => l.itemId);
    if (lines.length === 0) errors.lines = 'Add at least one dish.';
    else if (f.lines.some((l) => !l.itemId)) errors.lines = 'Choose a dish on every row, or remove the empty row.';
    else if (lines.some((l) => !Number.isInteger(Number(l.quantity)) || Number(l.quantity) < 1 || Number(l.quantity) > 999)) errors.lines = 'A quantity is 1 to 999.';
    return errors;
  }, []);

  const onSave = useCallback(async (f: RequisitionForm, m: FormModalMode, r: DineRequisition | null) => {
    const body = {
      dineOutletId: f.dineOutletId,
      requestingUnitId: f.requestingUnitId,
      purpose: f.purpose,
      neededAt: f.neededAt ? new Date(f.neededAt).toISOString() : null,
      remarks: f.remarks.trim() || null,
      lines: f.lines.filter((l) => l.itemId).map((l) => ({ itemId: l.itemId, quantity: Number(l.quantity), note: l.note.trim() || null })),
      rowVersion: m === 'edit' && r ? r.rowVersion : 0,
    };
    if (m === 'edit' && r) await dineOpsService.updateRequisition(r.id, body);
    else await dineOpsService.createRequisition(body);
  }, []);

  const outletOptions: SelectOption[] = useMemo(
    () => outlets.map((o) => ({ value: o.id, label: `${o.outletName} (${o.outletCode})${o.propertyName ? ` · ${o.propertyName}` : ''}` })), [outlets]);

  return (
    <EnterpriseFormModal<RequisitionForm, DineRequisition>
      isOpen={isOpen}
      mode={mode}
      record={record}
      noun="Requisition"
      createVerb="Raise"
      emptyForm={EMPTY_FORM}
      populateOnOpen={populateOnOpen}
      validate={validate}
      onSave={onSave}
      confirmSaveMessage={(m, f) => {
        const n = f.lines.filter((l) => l.itemId).length;
        return m === 'edit' ? `Save the requisition with ${n} dish(es)?` : `Raise a requisition for ${n} dish(es)? It goes to the kitchen when the outlet sends it.`;
      }}
      size="lg"
      tabId={tabId}
      moduleName={moduleName}
      createInstanceId={createInstanceId}
      recordKey={record?.id}
      getStripFormLabel={(f) => outlets.find((o) => o.id === f.dineOutletId)?.outletName}
      getStripRecordLabel={(r) => (r ? r.requisitionNumber : undefined)}
      onClose={onClose}
      onSaved={onSaved}
      onRestoreModal={onRestoreModal}
    >
      {(args) => <FormBody {...args} outlets={outlets} outletOptions={outletOptions} />}
    </EnterpriseFormModal>
  );
};

/** The body, as its own component so the outlet's card loads when the outlet changes. */
const FormBody: React.FC<{
  form: RequisitionForm;
  setForm: React.Dispatch<React.SetStateAction<RequisitionForm>>;
  mode: FormModalMode;
  record: DineRequisition | null;
  isSaving: boolean;
  getFieldError: (field: string) => string | undefined;
  clearFieldError: (field: string) => void;
  outlets: DineOutletAdmin[];
  outletOptions: SelectOption[];
}> = ({ form, setForm, mode, record, isSaving, getFieldError, clearFieldError, outlets, outletOptions }) => {
  const [options, setOptions] = useState<RequisitionItemOption[]>([]);
  const [loading, setLoading] = useState(false);
  // Active purposes only; one the record already has stays selectable even if it was deactivated since.
  const { purposes } = useRequisitionPurposes();
  const purposeChoices = useMemo(() => purposeOptions(purposes,
    record && mode !== 'create' ? { code: record.purpose, name: record.purposeName } : undefined), [purposes, record, mode]);

  useEffect(() => {
    if (!form.dineOutletId) { setOptions([]); return; }
    let live = true;
    setLoading(true);
    dineOpsService.getRequisitionItems(form.dineOutletId)
      .then((o) => { if (live) setOptions(o); })
      .catch(() => { if (live) setOptions([]); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [form.dineOutletId]);

  const set = <K extends keyof RequisitionForm>(key: K, value: RequisitionForm[K]) => {
    clearFieldError(key as string);
    setForm((prev) => ({ ...prev, [key]: value }));
  };
  const outlet = outlets.find((o) => o.id === form.dineOutletId);

  return (
    <>
      <div className="form-grid-2">
        <div className="form-group" data-field="dineOutletId">
          <SyutSelect label="Prepared by (outlet)" options={outletOptions} value={form.dineOutletId} isClearable={false}
            isDisabled={isSaving || mode === 'edit'} menuPortalTarget={document.body} required
            // Another outlet has another card and another entity: the dishes and the department start again.
            onChange={(v) => { clearFieldError('dineOutletId'); setForm((prev) => ({ ...prev, dineOutletId: (v as string) || '', requestingUnitId: '', lines: [blankLine()] })); }}
            error={getFieldError('dineOutletId')}
            helperText={mode === 'edit' ? 'Fixed once raised. Cancel and raise again for another outlet.' : 'Whose kitchen makes it. Its card and transfer prices apply.'} />
        </div>
        <div className="form-group" data-field="requestingUnitId">
          <BusinessUnitSelect
            key={form.dineOutletId || 'none'}
            label="For (department)"
            value={form.requestingUnitId}
            onChange={(v) => set('requestingUnitId', v)}
            entityId={outlet?.entityId}
            propertyId={outlet?.propertyId}
            types={REQUESTING_UNIT_TYPES}
            seed={record && mode !== 'create' && form.requestingUnitId === record.requestingUnitId
              ? { id: record.requestingUnitId, name: record.requestingUnitName } : undefined}
            isDisabled={isSaving || !form.dineOutletId}
            required
            error={getFieldError('requestingUnitId')}
            placeholder={form.dineOutletId ? 'Choose the department' : 'Choose the outlet first'}
            helperText="The department or cost centre that takes it, e.g. Front Office for welcome drinks."
          />
        </div>
        <div className="form-group" data-field="purpose">
          <SyutSelect label="Purpose" options={purposeChoices} value={form.purpose} isClearable={false} isDisabled={isSaving}
            menuPortalTarget={document.body} required error={getFieldError('purpose')} placeholder="Choose the purpose"
            onChange={(v) => set('purpose', ((v as string) || '') as RequisitionPurpose)} />
        </div>
        <div className="form-group" data-field="neededAt">
          <label className="form-label">Needed at</label>
          <input type="datetime-local" className="form-input" value={form.neededAt} disabled={isSaving}
            onChange={(e) => set('neededAt', e.target.value)} />
          <FieldHint>Optional. The list puts the soonest first.</FieldHint>
        </div>
        <div className="form-group" data-field="remarks" style={{ gridColumn: '1 / -1' }}>
          <label className="form-label">Remarks</label>
          <input className={`form-input${getFieldError('remarks') ? ' form-field-error' : ''}`} value={form.remarks} maxLength={500}
            disabled={isSaving} placeholder="e.g., Night shift, 20 staff; Group arrival 4 pm"
            onChange={(e) => set('remarks', e.target.value)} />
          {getFieldError('remarks') && <div className="form-error-message">{getFieldError('remarks')}</div>}
        </div>
      </div>
      <LinesEditor
        rows={form.lines}
        onChange={(rows) => { clearFieldError('lines'); setForm((prev) => ({ ...prev, lines: rows })); }}
        options={options}
        loading={loading}
        disabled={isSaving}
        error={getFieldError('lines')}
        outletChosen={!!form.dineOutletId}
      />
    </>
  );
};

export default DineOpsRequisitionFormModal;
